import type { z } from "zod";
import { formatEnvError } from "./errors";
import {
  agentsServerEnvSchema,
  chatApiEnvSchema,
  convexEnvSchema,
  triggerEnvSchema,
  twilioEnvSchema,
  voicebotEnvSchema,
  widgetServerEnvSchema,
  widgetToolingEnvSchema,
} from "./schema/runtimes";

function parseWithSchema<TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  rawEnv: Record<string, unknown>,
  profileName: string,
): z.infer<TSchema> {
  const parsed = schema.safeParse(rawEnv);

  if (!parsed.success) {
    const message = formatEnvError(parsed.error, rawEnv);
    throw new Error(`[${profileName}] ${message}`);
  }

  return parsed.data;
}

export function parseTriggerEnv(rawEnv: Record<string, unknown>): z.infer<typeof triggerEnvSchema> {
  return parseWithSchema(triggerEnvSchema, rawEnv, "trigger");
}

export function parseAgentsServerEnv(rawEnv: Record<string, unknown>): z.infer<typeof agentsServerEnvSchema> {
  return parseWithSchema(agentsServerEnvSchema, rawEnv, "agents");
}

export function parseChatApiEnv(rawEnv: Record<string, unknown>): z.infer<typeof chatApiEnvSchema> {
  return parseWithSchema(chatApiEnvSchema, rawEnv, "chat-api");
}

export function parseTwilioEnv(rawEnv: Record<string, unknown>): z.infer<typeof twilioEnvSchema> {
  return parseWithSchema(twilioEnvSchema, rawEnv, "twilio-service");
}

export function parseWidgetServerEnv(rawEnv: Record<string, unknown>): z.infer<typeof widgetServerEnvSchema> {
  return parseWithSchema(widgetServerEnvSchema, rawEnv, "chat-widget");
}

export function parseWidgetToolingEnv(rawEnv: Record<string, unknown>): z.infer<typeof widgetToolingEnvSchema> {
  return parseWithSchema(widgetToolingEnvSchema, rawEnv, "chat-widget-tooling");
}

export function parseVoicebotEnv(rawEnv: Record<string, unknown>): z.infer<typeof voicebotEnvSchema> {
  return parseWithSchema(voicebotEnvSchema, rawEnv, "voicebot");
}

export function parseConvexEnv(rawEnv: Record<string, unknown>): z.infer<typeof convexEnvSchema> {
  return parseWithSchema(convexEnvSchema, rawEnv, "convex");
}

export const parsers = {
  parseAgentsServerEnv,
  parseTriggerEnv,
  parseChatApiEnv,
  parseTwilioEnv,
  parseVoicebotEnv,
  parseWidgetServerEnv,
  parseWidgetToolingEnv,
  parseConvexEnv,
};
